/**
 * Kanzlei K4-B — field values out of Sentry FREE TEXT (plan
 * squishy-honking-mountain.md, "Gemeinsame Textmuster"). Key-based scrubbing
 * cannot see a value that sits inside a sentence ("Value 'Rot' is not a valid
 * option"), a console line or a URL; these rules can.
 *
 * Byte-identical copy in every Worker and frontend that carries it. The
 * regexes are the plan's canonical table — the same ones the Java
 * SentryPiiScrubber carries. Change one copy, change all. Dependency-free.
 *
 *   T1–T7, T10, T11  value patterns: only the quoted part becomes
 *                `[redacted]`. Multi-line and bounded (`[\s\S]{0,2000}`), no
 *                nested quantifiers, so the cost per opener is at most 2000
 *                steps. Exact patterns first; then, for every opener still not
 *                redacted (the SDK cut the text at 250 chars, or the value is
 *                longer than 2000), the open-ended fallback: the rest of the
 *                string goes. T11 (v2.2) is java.time: `Invalid date '…'( as
 *                '…')`, `Invalid value for <field> (valid values …): <n>` —
 *                also as the tail of `Text '…' could not be parsed: …`.
 *   T9           `key=value` and `"key": "value"` of the listed keys; `code`
 *                (v2.2) keeps a pure error code (`11000`, `card_declined`,
 *                `ECONNRESET`), any other value goes, and its `key=value`
 *                form ends at a space.
 *   token        the portal bearer token in `/p/<token>`, `/portal/<token>`.
 *   email        free text only, the Java rule (`[email-redacted]`); v2.2: a
 *                run of address characters holding an address goes whole, so
 *                glued addresses (`a@b.de_c@d.de`, `a@b.com+x@y.org`) both go.
 *   phone        free text only (`[phone-redacted]`), v2.1: a phone SHAPE
 *                — `+` and 7–15 digits, or separated groups of 8+ digits —
 *                never touching a word character, never an ISO date; bare
 *                digit runs, epoch stamps and ids stay.
 *   U1           URL rule: query + fragment always go; object-store and foreign
 *                hosts keep origin + first path segment; own hosts keep the path
 *                with the portal token masked; userinfo never survives;
 *                `data:` URLs keep their media type only.
 *
 * Every entry point caps its string at 16 KB first, and every regex runs only
 * when the text contains its literal prefix. The structure walk reads each
 * object once, at most 200 items / keys per level and 5000 objects in all.
 */

export const VALUE_REDACTED = '[redacted]'
export const EMAIL_REDACTED = '[email-redacted]'
export const PHONE_REDACTED = '[phone-redacted]'
/** The message of an event whose scrub threw (see `failClosedEvent`). */
export const SCRUB_FAILED = '[scrub-failed]'
const CIRCULAR = '[Circular]'
const BINARY = '[binary]'

// --- cap --------------------------------------------------------------------

/** Longest string any rule looks at; the rest is cut before the first regex. */
export const MAX_TEXT_LENGTH = 16 * 1024
const TRUNCATED = '…[truncated]'

/** At most 16 KB, then `…[truncated]`; a surrogate pair is never split. Idempotent. */
export function capText(text: string): string {
  if (text.length <= MAX_TEXT_LENGTH) return text
  if (text.length <= MAX_TEXT_LENGTH + TRUNCATED.length && text.endsWith(TRUNCATED)) return text
  let end = MAX_TEXT_LENGTH
  const last = text.charCodeAt(end - 1)
  if (last >= 0xd800 && last <= 0xdbff) end -= 1
  return text.slice(0, end) + TRUNCATED
}

// --- value patterns -----------------------------------------------------------

/** A regex, what its match becomes, and the literal the text must contain first. */
type Rule = readonly [hint: string, re: RegExp, replacement: string | ((match: string, ...groups: string[]) => string)]

/** T1–T7 + T10 + T11, exact form, in this order. Every regex is global. */
const EXACT_RULES: readonly Rule[] = [
  // T1 — Mongo E11000 `dup key: { … }`, greedy to the last `}` in reach.
  ['dup key:', /(dup key:\s*\{)[\s\S]{0,2000}\}/g, '$1[redacted]}'],
  // T2 — `Text '…' could not be parsed`, lazy.
  ["Text '", /(Text ')[\s\S]{0,2000}?(' could not be parsed)/g, '$1[redacted]$2'],
  // T3 — `For input string: "…"` (+ ` under radix N`, kept), greedy.
  ['For input string: "', /(For input string: ")[\s\S]{0,2000}"( under radix \d+)?/g, '$1[redacted]"$2'],
  // T4 — `Unparseable date: "…"`, greedy.
  ['Unparseable date: "', /(Unparseable date: ")[\s\S]{0,2000}"/g, '$1[redacted]"'],
  // T5 — `Invalid value '…' for field` / `Invalid value "…" for field`, lazy.
  ['Invalid value ', /(Invalid value (['"]))[\s\S]{0,2000}?(\2 for field)/g, '$1[redacted]$3'],
  // T6 — `Value '…' is not a valid option` / `Value "…" is not a valid option`, lazy.
  ['Value ', /(Value (['"]))[\s\S]{0,2000}?(\2 is not a valid option)/g, '$1[redacted]$3'],
  // T7 — main-consumer's `UPDATE_GUEST: (value )"…" is not a …`, lazy.
  ['UPDATE_GUEST: ', /(UPDATE_GUEST: (?:value )?")[\s\S]{0,2000}?(" is not a)/g, '$1[redacted]$2'],
  // T10 — V8 JSON.parse (also `Request.json()`): `Unexpected token 'x', "…" is
  // not valid JSON`, the source window optionally `...`-framed; the one token
  // character stays.
  [
    'Unexpected token ',
    /(Unexpected token '[\s\S]{1,2}', (?:\.\.\.)?")[\s\S]{0,2000}?("(?:\.\.\.)? is not valid JSON)/g,
    '$1[redacted]$2',
  ],
  // T10 — the special-string form `"…" is not valid JSON`.
  ['is not valid JSON', /"[^"]{0,2000}"((?:\.\.\.)? is not valid JSON)/g, '"[redacted]"$1'],
  // T10 — `BigInt("…")`: `Cannot convert … to a BigInt` (the operand unquoted).
  [' to a BigInt', /(Cannot convert )[\s\S]{0,2000}?( to a BigInt)/g, '$1[redacted]$2'],
  // T11 — java.time `LocalDate.create`: `Invalid date 'February 29' as '1990'
  // is not a leap year` (both quotes), then `Invalid date 'APRIL 31'`. After
  // T2, so the tail of `Text '…' could not be parsed: …` is reached too.
  ["Invalid date '", /(Invalid date ')[^']{0,2000}(' as ')[^']{0,2000}(')/g, '$1[redacted]$2[redacted]$3'],
  ["Invalid date '", /(Invalid date ')(?!\[redacted\]')[^']{0,2000}(')/g, '$1[redacted]$2'],
  // T11 — `ValueRange.checkValidValue`: `Invalid value for MonthOfYear (valid
  // values 1 - 12): 13`; field and range stay, the number goes.
  ['Invalid value for ', /(Invalid value for \w{1,100} \(valid values [^()\r\n]{0,200}\): )-?\d+/g, '$1[redacted]'],
]

/**
 * The open-ended fallbacks: the first opener NOT followed by its exact
 * replacement — no suffix in reach — takes the rest of the string with it.
 * Run after every exact rule. Not global: the first such opener ends the text.
 */
const FALLBACK_RULES: readonly Rule[] = [
  ['dup key:', /(dup key:\s*\{)(?!\[redacted\]\})[\s\S]*/, '$1[redacted]'],
  ["Text '", /(Text ')(?!\[redacted\]' could not be parsed)[\s\S]*/, '$1[redacted]'],
  ['For input string: "', /(For input string: ")(?!\[redacted\]")[\s\S]*/, '$1[redacted]'],
  ['Unparseable date: "', /(Unparseable date: ")(?!\[redacted\]")[\s\S]*/, '$1[redacted]'],
  ['Invalid value ', /(Invalid value (['"]))(?!\[redacted\]\2 for field)[\s\S]*/, '$1[redacted]'],
  ['Value ', /(Value (['"]))(?!\[redacted\]\2 is not a valid option)[\s\S]*/, '$1[redacted]'],
  ['UPDATE_GUEST: ', /(UPDATE_GUEST: (?:value )?")(?!\[redacted\]" is not a)[\s\S]*/, '$1[redacted]'],
  [
    'Unexpected token ',
    /(Unexpected token '[\s\S]{1,2}', (?:\.\.\.)?")(?!\[redacted\]"(?:\.\.\.)? is not valid JSON)[\s\S]*/,
    '$1[redacted]',
  ],
  ["Invalid date '", /(Invalid date ')(?!\[redacted\]')[\s\S]*/, '$1[redacted]'],
  ["' as '", /(Invalid date '\[redacted\]' as ')(?!\[redacted\]')[\s\S]*/, '$1[redacted]'],
]

/** T9 keys, case-insensitive, after an optional `<word>_` prefix (`code` has its own rule below). */
const T9_KEYS = String.raw`credentials|credential\d*|token|access_?token|session_?token|id_?token|refresh_?token|sig|q|search|search_?term|filters|email|guest_?email|first_?name|last_?name`
const T9_KEY = String.raw`(?:[a-z0-9]+_)?(?:${T9_KEYS})`
/** T9 `code` (v2.2), after an optional `<word>_` prefix (`error_code`, `decline_code`). */
const CODE_KEY = String.raw`(?:[a-z0-9]+_)?code`
/** What a `code` may keep: a pure error code — 1–5 digits (Mongo `11000`) or 2–40 letters and `_` (`card_declined`, `ECONNRESET`, `SES_THROTTLED`). */
const ERROR_CODE = /^(?:\d{1,5}|[A-Za-z_]{2,40})$/

function keepErrorCode(match: string, lead: string, value: string): string {
  return ERROR_CODE.test(value) ? match : `${lead}[redacted]`
}

function keepQuotedErrorCode(match: string, lead: string, value: string): string {
  return ERROR_CODE.test(value) ? match : `${lead}[redacted]"`
}

/** T9 — `key=value`: the value runs on over spaces, up to & # quote < > or a line break. */
const KEY_RULES: readonly Rule[] = [
  ['=', new RegExp(String.raw`\b(${T9_KEY})=[^&#\r\n"'<>]*`, 'gi'), '$1=[redacted]'],
  // T9 `code=`: the value ends at a space too — an error code is one word, and
  // the rest of the line is not its value.
  ['=', new RegExp(String.raw`\b(${CODE_KEY}=)([^&#\s"'<>]*)`, 'gi'), keepErrorCode],
  // T9 — `"key": "value"` (JSON, also mid-text); escaped quotes stay inside.
  ['"', new RegExp(String.raw`("${T9_KEY}"\s*:\s*")(?:[^"\\]|\\[\s\S]){0,2000}"`, 'gi'), '$1[redacted]"'],
  ['"', new RegExp(String.raw`("${CODE_KEY}"\s*:\s*")((?:[^"\\]|\\[\s\S]){0,2000})"`, 'gi'), keepQuotedErrorCode],
  // … and the fallbacks: an unterminated (cut) string value takes the rest.
  ['"', new RegExp(String.raw`("${T9_KEY}"\s*:\s*")(?!\[redacted\]")[\s\S]*`, 'i'), '$1[redacted]'],
  [
    '"',
    new RegExp(String.raw`("${CODE_KEY}"\s*:\s*")(?!\[redacted\]")(?!(?:\d{1,5}|[A-Za-z_]{2,40})")[\s\S]*`, 'i'),
    '$1[redacted]',
  ],
]

/**
 * E-mail v2.2: a RUN of address characters (local-part characters and `@`) is
 * one unit, and a run that holds a Java-rule address — a local character, `@`,
 * a label, a dot, a label character — goes whole. Glued addresses
 * (`a@b.de_c@d.de`, `a@b.com+x@y.org`, `a@b.com..x@y.org`) are one run, so
 * both go; the per-address rule stopped after the first domain and left the
 * second. No lookbehind (older Safari cannot parse one, and the frontends
 * carry this file); one pass over the runs plus one scan per run: linear.
 */
const ADDRESS_RUN = /[A-Za-z0-9_.+@-]+/g
const ADDRESS_IN_RUN = /[A-Za-z0-9_.+-]@[A-Za-z0-9-]+\.[A-Za-z0-9-]/

function redactAddressRun(run: string): string {
  if (!run.includes('@') || !ADDRESS_IN_RUN.test(run)) return run
  // A sentence's full stop after the address stays.
  let end = run.length
  while (end > 0 && run.charCodeAt(end - 1) === 46) end -= 1
  return EMAIL_REDACTED + run.slice(end)
}
/**
 * Phone v2.1 (narrower than Java's `\+?\d[\d \-]{6,}\d`, which ate ISO dates,
 * epoch stamps and digit runs inside ids). A match never touches
 * `[A-Za-z0-9_]` on either side (the lead character is captured and
 * re-emitted: no lookbehind), and never starts or ends at a `-` that joins it
 * to a word (UUID groups). Two shapes:
 *   - `+` then 7–15 digits, each after at most two of `space . / ( ) -`;
 *   - no `+`: digit groups joined by 1–3 of `space / ( ) -` (no dot: decimals,
 *     versions, IPs), at least one join, 8+ digits in all (counted in
 *     `phoneOrKeep`); an optional `(` in front; never an ISO date start.
 * Each shape is a chain with one way to split it, so a start costs O(length).
 */
const PHONE =
  /(^|[^A-Za-z0-9_-])(\+(?:[ ./()-]{0,2}\d){7,15}|\(?(?!\d{4}-\d{2}-\d{2}(?!\d))\d+(?:[ /()-]{1,3}\d+)+)(?![A-Za-z0-9_]|-[A-Za-z0-9])/g

function phoneOrKeep(match: string, lead: string, phone: string): string {
  if (phone.startsWith('+')) return lead + PHONE_REDACTED
  let digits = 0
  for (let i = 0; i < phone.length; i++) {
    const c = phone.charCodeAt(i)
    if (c >= 48 && c <= 57) digits += 1
  }
  if (digits < 8) return match
  // `(0170 1234567)`: an opening paren the number never closes is punctuation.
  return phone.startsWith('(') && !phone.includes(')') ? `${lead}(${PHONE_REDACTED}` : lead + PHONE_REDACTED
}

function applyRules(text: string, rules: readonly Rule[]): string {
  let out = text
  for (const [hint, re, replacement] of rules) {
    if (!out.includes(hint)) continue
    out = typeof replacement === 'string' ? out.replace(re, replacement) : out.replace(re, replacement)
  }
  return out
}

/** T1–T7 + T10 + T11 (exact, then fallbacks), T9, the portal token — no length cap. */
function valueText(text: string): string {
  let out = applyRules(text, EXACT_RULES)
  out = applyRules(out, FALLBACK_RULES)
  out = applyRules(out, KEY_RULES)
  return maskTokenPath(out)
}

/** T1–T7 + T10 + T11 + T9 + the portal token on one string (no email / phone rule: log-safe). */
export function scrubValueText(text: string): string {
  return valueText(capText(text))
}

// --- U1 ---------------------------------------------------------------------

/** Object stores: host + first segment (bucket / key prefix), never the key. */
const OBJECT_STORE_HOST =
  /(?:^|\.)(?:r2\.cloudflarestorage\.com|r2\.dev|amazonaws\.com|storage\.googleapis\.com|blob\.core\.windows\.net)$|^(?:[a-z0-9-]+-)?assets\.veltric\.app$/i
/** Own hosts keep their path; every other host is foreign (webhooks, tenant domains). */
const OWN_HOST = /(?:^|\.)veltric\.app$/i
/** SES click tracking: the path IS the destination URL with the guest's token — first segment only. */
const TRACKING_HOST = /^t\.veltric\.app$/i
/** Portal bearer token in a path that starts at a space / `(` / the start: any length. */
const TOKEN_PATH = /(^|[\s(])(\/(?:p|portal)\/)[^/?#\s"'<>)]+/g
/** … and anywhere after a non-word character: 8+ token characters. */
const TOKEN_ANYWHERE = /(^|[^A-Za-z0-9_])(\/(?:p|portal)\/)[\w.~%+=-]{8,}/g
/** http(s) and ws(s): parsed with `URL`. */
const WEB_URL = /^(?:https?|wss?):\/\//i
/** Any `scheme://authority/path` (also unparseable or protocol-relative): userinfo, query, fragment, deep path go. */
const AUTHORITY_URL = /^([a-z][a-z0-9+.-]{0,31}:)?\/\/([^/?#]*)([^?#]*)/i
const USERINFO = /\/\/[^/?#\s@]*@/g
const HOSTLIKE_AFTER_SLASHES = /^\/\/(?:[^/?#@]*@)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?::\d+)?(?:[/?#]|$)/
const SCHEME_URL_IN_TEXT = /\b[a-z][a-z0-9+.-]{0,31}:\/\/[^\s"'<>`]+/gi
const PROTOCOL_RELATIVE_IN_TEXT = /(^|[\s"'(=,<>[])(\/\/[^\s"'<>`]+)/g
const DATA_URL = /^data:([\w.+-]{1,127}\/[\w.+-]{1,127})?/i
/** A `data:` URL in a text: `data:` + optional media type + `;` or `,`, up to the next delimiter. One pass, linear. */
const DATA_URL_IN_TEXT = /\bdata:([\w.+-]{1,127}\/[\w.+-]{1,127})?[;,][^\s"'<>`]*/gi

function stripQueryAndFragment(s: string): string {
  const cut = s.search(/[?#]/)
  return cut < 0 ? s : s.slice(0, cut)
}

/** `/portal/<token>/ticket` → `/portal/[token]/ticket` (also inside "GET /p/<token>" and mid-text). */
export function maskTokenPath(path: string): string {
  let out = path
  if (out.includes('/p/') || out.includes('/portal/')) {
    out = out.replace(TOKEN_PATH, '$1$2[token]').replace(TOKEN_ANYWHERE, '$1$2[token]')
  }
  return out
}

function parseWebUrl(s: string): URL | undefined {
  const relative = s.startsWith('//')
  if (!relative && !WEB_URL.test(s)) return undefined
  try {
    return new URL(relative ? `https:${s}` : s)
  } catch {
    return undefined
  }
}

/** U1 on one URL (absolute, protocol-relative, relative, `data:`, or a "METHOD url" span name). */
export function scrubUrl(raw: string): string {
  const s = capText(raw)
  const data = DATA_URL.exec(s)
  if (data) return `data:${data[1] ?? ''}`
  const url = parseWebUrl(s)
  if (url) {
    // `host` keeps a port; user:password never makes it into the origin.
    const origin = `${s.startsWith('//') ? '' : url.protocol}//${url.host}`
    const host = url.hostname.toLowerCase()
    if (OWN_HOST.test(host) && !OBJECT_STORE_HOST.test(host) && !TRACKING_HOST.test(host)) {
      return origin + maskTokenPath(url.pathname)
    }
    const first = url.pathname.split('/').find((segment) => segment !== '')
    return first === undefined ? origin : `${origin}/${first}`
  }
  const authority = AUTHORITY_URL.exec(s)
  if (authority) {
    // Unparseable or another scheme (mongodb+srv://user:pw@…): the foreign rule.
    const host = authority[2]!.slice(authority[2]!.lastIndexOf('@') + 1)
    const first = authority[3]!.split('/').find((segment) => segment !== '')
    return `${authority[1] ?? ''}//${host}${first === undefined ? '' : `/${first}`}`
  }
  return maskTokenPath(stripQueryAndFragment(s.includes('//') ? s.replace(USERINFO, '//') : s))
}

function urlsInText(text: string): string {
  let out = text
  if (/data:/i.test(out)) out = out.replace(DATA_URL_IN_TEXT, (_m, type: string | undefined) => `data:${type ?? ''}`)
  if (out.includes('//')) {
    out = out.replace(SCHEME_URL_IN_TEXT, (match) => scrubUrl(match))
    out = out.replace(PROTOCOL_RELATIVE_IN_TEXT, (match, lead: string, url: string) =>
      HOSTLIKE_AFTER_SLASHES.test(url) ? lead + scrubUrl(url) : match,
    )
  }
  return out
}

/** U1 on every URL inside a text (any `scheme://`, protocol-relative `//host`, `data:`). */
export function scrubUrlsInText(text: string): string {
  return urlsInText(capText(text))
}

function freeText(text: string): string {
  let out = valueText(urlsInText(text))
  if (out.includes('@')) out = out.replace(ADDRESS_RUN, redactAddressRun)
  return out.replace(PHONE, phoneOrKeep)
}

/** U1, then T1–T7 + T10 + T11 + T9 + the portal token, then email + phone — for console lines and all other free text. */
export function scrubFreeText(text: string): string {
  return freeText(capText(text))
}

// --- structure walkers ------------------------------------------------------

const MAX_DEPTH = 8
/** Items per array / Set / Map and keys per object the walk reads; the rest is one `[truncated]`. */
const MAX_BREADTH = 200
/** Objects one walk reads in all (getters and `toJSON` can make new ones forever); past it, `[redacted]`. */
const MAX_NODES = 5000
const TRUNCATED_ITEMS = '[truncated]'
/** A JSON log line up to this length is parsed and scrubbed string by string. */
const MAX_JSON_LENGTH = 256 * 1024

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Maps every string inside `value` through `fn`, failing CLOSED: deeper than
 * 8 levels `[redacted]`, a cycle `[Circular]`, binary `[binary]`. An Error
 * becomes its `name: message` string, a URL its scrubbed href; Map / Set
 * become arrays, `toJSON` is honoured, any other class instance is walked by
 * its own enumerable properties (as Sentry serialises it). A Date stays.
 *
 * Bounded (v2.2) — a DOM node, a React fiber or `window` must not freeze the
 * walk: each object is walked ONCE (a shared one gets the result of its first
 * walk again), at most 200 items / keys per level (then one `[truncated]`),
 * at most 5000 objects in all (then `[redacted]`).
 */
export function deepMapStrings(value: unknown, fn: (s: string) => string): unknown {
  return walk(value, 0, { fn, ancestors: new WeakSet(), done: new WeakMap(), nodes: 0 })
}

interface Walk {
  readonly fn: (s: string) => string
  /** The objects on the current path: one met again is a cycle. */
  readonly ancestors: WeakSet<object>
  /** Every object walked to the end, with what it became. */
  readonly done: WeakMap<object, unknown>
  nodes: number
}

function walk(value: unknown, depth: number, w: Walk): unknown {
  if (typeof value === 'string') return w.fn(value)
  if (value === null || typeof value !== 'object') return value
  if (depth > MAX_DEPTH) return VALUE_REDACTED
  if (w.ancestors.has(value)) return CIRCULAR
  if (w.done.has(value)) return w.done.get(value)
  if (value instanceof Error) return w.fn(`${value.name}: ${value.message}`)
  if (value instanceof URL) return scrubUrl(value.href)
  if (value instanceof Date) return value
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) return BINARY
  if (w.nodes >= MAX_NODES) return VALUE_REDACTED
  w.nodes += 1
  w.ancestors.add(value)
  try {
    const out = walkObject(value, depth, w)
    w.done.set(value, out)
    return out
  } finally {
    w.ancestors.delete(value)
  }
}

function walkObject(value: object, depth: number, w: Walk): unknown {
  const next = (v: unknown) => walk(v, depth + 1, w)
  if (Array.isArray(value)) {
    const out: unknown[] = []
    const n = Math.min(value.length, MAX_BREADTH)
    for (let i = 0; i < n; i++) out.push(next(value[i]))
    if (value.length > MAX_BREADTH) out.push(TRUNCATED_ITEMS)
    return out
  }
  if (value instanceof Map || value instanceof Set) {
    const out: unknown[] = []
    for (const item of value.entries()) {
      if (out.length === MAX_BREADTH) {
        out.push(TRUNCATED_ITEMS)
        break
      }
      out.push(value instanceof Map ? [next(item[0]), next(item[1])] : next(item[0]))
    }
    return out
  }
  const toJSON: unknown = (value as { toJSON?: unknown }).toJSON
  if (typeof toJSON === 'function') return next(toJSON.call(value))
  const out: Record<string, unknown> = {}
  const keys = Object.keys(value)
  const n = Math.min(keys.length, MAX_BREADTH)
  for (let i = 0; i < n; i++) {
    const k = keys[i] as string
    out[k] = next((value as Record<string, unknown>)[k])
  }
  if (keys.length > MAX_BREADTH) out[TRUNCATED_ITEMS] = TRUNCATED_ITEMS
  return out
}

/**
 * A console line: when it is a JSON log line, every string inside it is
 * scrubbed BEFORE re-serialising (in the raw line the quotes of T3/T4/T7 are
 * escaped and would not match); then the free-text rules on the whole text.
 */
export function scrubLogText(text: string): string {
  const t = text.trim()
  if (
    t.length <= MAX_JSON_LENGTH &&
    ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']')))
  ) {
    try {
      return scrubFreeText(JSON.stringify(deepMapStrings(JSON.parse(t), scrubFreeText)))
    } catch {
      // not JSON — plain text below
    }
  }
  return scrubFreeText(text)
}

const URL_KEY = /^(?:url|http\.url|url\.full)$/i
const PATH_KEY = /^(?:url\.path|http\.target)$/i
const QUERY_KEY = /^(?:http|url)\.(?:query|fragment)$/i

/** Span / breadcrumb `data`: URL keys through U1, query + fragment keys dropped, the rest through `text`. */
function scrubUrlData(data: Record<string, unknown>, text: (s: string) => string): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(data)) {
    if (QUERY_KEY.test(k)) continue
    if (typeof v === 'string' && URL_KEY.test(k)) out[k] = scrubUrl(v)
    else if (typeof v === 'string' && PATH_KEY.test(k)) out[k] = maskTokenPath(stripQueryAndFragment(v))
    else out[k] = deepMapStrings(v, text)
  }
  return out
}

/**
 * Breadcrumb: a console crumb's raw `data.arguments` go (objects and all);
 * `message` and every remaining string in `data` through the free-text rules,
 * URL keys through U1.
 */
export function scrubBreadcrumbText<T extends object>(crumb: T): T {
  const b = { ...crumb } as Record<string, unknown>
  if (typeof b.message === 'string') b.message = scrubLogText(b.message)
  if (isRecord(b.data)) {
    const data = { ...b.data }
    if (b.category === 'console') delete data.arguments
    b.data = scrubUrlData(data, scrubLogText)
  }
  return b as T
}

/** Transaction / span names and descriptions: the free-text rules, the portal token masked. */
function scrubSpanName(name: string): string {
  return maskTokenPath(scrubFreeText(name))
}

function scrubSpan(span: unknown): unknown {
  if (!isRecord(span)) return span
  const s = { ...span }
  if (typeof s.description === 'string') s.description = scrubSpanName(s.description)
  if (isRecord(s.data)) s.data = scrubUrlData(s.data, scrubSpanName)
  return s
}

function scrubRequest(request: Record<string, unknown>): Record<string, unknown> {
  const r = { ...request }
  if (typeof r.url === 'string') r.url = scrubUrl(r.url)
  delete r.query_string
  if (typeof r.data === 'string') r.data = scrubLogText(r.data)
  else if (r.data !== undefined) r.data = deepMapStrings(r.data, scrubFreeText)
  if (isRecord(r.headers)) {
    const headers = { ...r.headers }
    for (const k of Object.keys(headers)) {
      const v = headers[k]
      if (typeof v === 'string' && /^referer$/i.test(k)) headers[k] = scrubUrl(v)
    }
    r.headers = headers
  }
  return r
}

/** Trace / span ids are digit-rich hex: structure, never free text. */
const TRACE_ID_KEYS = ['trace_id', 'span_id', 'parent_span_id'] as const
const HEX_ID = /^[0-9a-f]{16,32}$/i

function scrubContexts(contexts: Record<string, unknown>): Record<string, unknown> {
  const out = deepMapStrings(contexts, scrubFreeText) as Record<string, unknown>
  const trace = contexts.trace
  if (isRecord(trace) && isRecord(out.trace)) {
    const t = { ...out.trace }
    for (const k of TRACE_ID_KEYS) {
      const id = trace[k]
      if (typeof id === 'string' && HEX_ID.test(id)) t[k] = id
    }
    if (isRecord(trace.data)) t.data = scrubUrlData(trace.data, scrubSpanName)
    out.trace = t
  }
  return out
}

/**
 * The whole event — error, message or transaction — through the free-text
 * rules (U1 + T + token + email + phone): `message`, `logentry`,
 * `exception.values[].value`, `extra` / `tags` / `contexts` (trace ids kept),
 * `breadcrumbs`, `request` (URL + Referer through U1, query string gone, body
 * scrubbed), the transaction name, span descriptions and span / trace `data`
 * URLs. Never mutates its input.
 */
export function scrubEventText<T extends object>(event: T): T {
  const e = { ...event } as Record<string, unknown>
  if (typeof e.message === 'string') e.message = scrubFreeText(e.message)
  if (isRecord(e.logentry)) e.logentry = deepMapStrings(e.logentry, scrubFreeText)
  if (isRecord(e.exception) && Array.isArray(e.exception.values)) {
    e.exception = {
      ...e.exception,
      values: e.exception.values.map((v: unknown) =>
        isRecord(v) && typeof v.value === 'string' ? { ...v, value: scrubFreeText(v.value) } : v,
      ),
    }
  }
  if (isRecord(e.extra)) e.extra = deepMapStrings(e.extra, scrubFreeText)
  if (isRecord(e.tags)) e.tags = deepMapStrings(e.tags, scrubFreeText)
  if (isRecord(e.contexts)) e.contexts = scrubContexts(e.contexts)
  if (isRecord(e.request)) e.request = scrubRequest(e.request)
  if (Array.isArray(e.breadcrumbs)) {
    e.breadcrumbs = e.breadcrumbs.map((b: unknown) => (isRecord(b) ? scrubBreadcrumbText(b) : b))
  }
  if (typeof e.transaction === 'string') e.transaction = scrubSpanName(e.transaction)
  if (Array.isArray(e.spans)) e.spans = e.spans.map(scrubSpan)
  return e as T
}

// --- fail closed ------------------------------------------------------------

const KEPT_ON_FAILURE = ['event_id', 'timestamp', 'level', 'platform', 'environment', 'release', 'dist'] as const
const ERROR_TYPE = /^[A-Za-z0-9_$.]{1,100}$/

/**
 * What a Sentry hook sends when its scrub threw — never the unscrubbed
 * original, and never throws. By event type (v2.2):
 *   - an error event (no `type`, or `error`): the envelope facts, the
 *     exception types and their `mechanism` flags, message `[scrub-failed]`,
 *     tag `pii_scrub: failed` — no breadcrumbs, extra, contexts, request, user
 *     or text;
 *   - a feedback: stays a feedback — `type`, the envelope facts, the tag, and
 *     `contexts.feedback.message` `[scrub-failed]` (its required field);
 *   - a transaction, and every other type: `null` — the caller drops it (a
 *     transaction without its trace is no transaction, and turning it into an
 *     error event made a scrub failure look like an app error).
 */
export function failClosedEvent<T extends object>(event: T): T | null {
  try {
    const e = event as Record<string, unknown>
    const type = e.type
    const feedback = type === 'feedback'
    if (typeof type === 'string' && type !== 'error' && !feedback) return null
    const out: Record<string, unknown> = {}
    if (feedback) out.type = type
    for (const k of KEPT_ON_FAILURE) {
      const v = e[k]
      if (typeof v === 'string' || typeof v === 'number') out[k] = v
    }
    if (isRecord(e.sdk)) out.sdk = { name: e.sdk.name, version: e.sdk.version }
    if (feedback) {
      out.contexts = { feedback: { message: SCRUB_FAILED } }
      out.tags = { pii_scrub: 'failed' }
      return out as T
    }
    out.message = SCRUB_FAILED
    const exception = e.exception
    if (isRecord(exception) && Array.isArray(exception.values)) {
      out.exception = {
        values: exception.values.map((v: unknown) => {
          const type = isRecord(v) && typeof v.type === 'string' && ERROR_TYPE.test(v.type) ? v.type : 'Error'
          const value: Record<string, unknown> = { type, value: SCRUB_FAILED }
          const mechanism = isRecord(v) ? v.mechanism : undefined
          if (isRecord(mechanism)) {
            value.mechanism = {
              type: typeof mechanism.type === 'string' && ERROR_TYPE.test(mechanism.type) ? mechanism.type : 'generic',
              handled: mechanism.handled === true,
            }
          }
          return value
        }),
      }
    }
    out.tags = { pii_scrub: 'failed' }
    return out as T
  } catch {
    return { message: SCRUB_FAILED, level: 'error', tags: { pii_scrub: 'failed' } } as unknown as T
  }
}
